import './Splitter.scss';
import { useCallback, type MouseEvent as ReactMouseEvent } from 'react';

export interface SplitterProps {
  /** Current pixel size of the controlled pane. */
  readonly size: number;
  /** Called with the new clamped size while dragging. */
  readonly onResize: (size: number) => void;
  /** Lower and upper bounds (px) for the controlled pane. */
  readonly min: number;
  readonly max: number;
  /**
   * Which side of the handle the controlled pane sits on. The pane grows as the
   * cursor moves away from it: 'before' (left/top) grows on a positive delta,
   * 'after' (right/bottom) grows on a negative delta.
   */
  readonly side: 'before' | 'after';
  /** Handle axis: 'vertical' resizes width (col-resize), 'horizontal' height (row-resize). */
  readonly orientation?: 'vertical' | 'horizontal';
  readonly className?: string;
  readonly ariaLabel?: string;
}

/**
 * A draggable divider that resizes an adjacent pane. The pane's size is owned by
 * the caller (applied as an inline width/height elsewhere); the Splitter only
 * reports the new clamped size via `onResize` during a drag.
 */
export const Splitter = ({ size, onResize, min, max, side, orientation = 'vertical', className, ariaLabel }: SplitterProps) => {
  const handleMouseDown = useCallback(
    (event: ReactMouseEvent) => {
      event.preventDefault();
      const vertical = orientation === 'vertical';
      const start = vertical ? event.clientX : event.clientY;
      const startSize = size;
      const grow = side === 'before' ? 1 : -1;
      const onMove = (moveEvent: MouseEvent) => {
        const current = vertical ? moveEvent.clientX : moveEvent.clientY;
        const next = startSize + grow * (current - start);
        onResize(Math.min(Math.max(next, min), max));
      };
      const onUp = () => {
        document.removeEventListener('mousemove', onMove);
        document.removeEventListener('mouseup', onUp);
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
      };
      document.addEventListener('mousemove', onMove);
      document.addEventListener('mouseup', onUp);
      document.body.style.cursor = vertical ? 'col-resize' : 'row-resize';
      document.body.style.userSelect = 'none';
    },
    [size, onResize, min, max, side, orientation]
  );

  return (
    <div
      className={`splitter splitter-${orientation}${className ? ` ${className}` : ''}`}
      onMouseDown={handleMouseDown}
      role="separator"
      aria-orientation={orientation}
      aria-label={ariaLabel}
    />
  );
};

export default Splitter;
